import { useMemo, useState } from 'react';
import { Calendar, CalendarDays, CalendarRange, Download, FileSpreadsheet, Loader2, X } from 'lucide-react';
import { useReunionMensuelleData } from '../../../hooks/useReunionMensuelleData';
import { Granularity, getPeriodBuckets } from '../../../utils/reunionMensuelle';
import { exportReunionMensuelle, ReunionExportSections } from '../../../utils/reunionExport';
import ScopeSelector, { Scope, getScopeColumns } from './ScopeSelector';

interface ExportReunionModalProps {
  fiscalYear: number;
  granularity: Granularity;
  scope: Scope;
  onClose: () => void;
}

const SELECT_CLS = 'px-3 py-2 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors cursor-pointer';

const SECTION_OPTIONS: { key: keyof ReunionExportSections; label: string }[] = [
  { key: 'delai', label: 'Délai' },
  { key: 'ventes', label: 'Ventes / Ventes moyennes' },
  { key: 'honoraires', label: "Moyenne d'honoraires" },
  { key: 'retrocession', label: 'Rétrocession' },
  { key: 'interagence', label: 'Inter-agence' },
];

export default function ExportReunionModal({ fiscalYear: initialFiscalYear, granularity: initialGranularity, scope: initialScope, onClose }: ExportReunionModalProps) {
  const [fiscalYear, setFiscalYear] = useState(initialFiscalYear);
  const [granularity, setGranularity] = useState<Granularity>(initialGranularity);
  const [scope, setScope] = useState<Scope>(initialScope);
  const [sections, setSections] = useState<ReunionExportSections>({
    delai: true, ventes: true, honoraires: true, retrocession: true, interagence: true,
  });
  const [includeDetails, setIncludeDetails] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const { transactions, agencies, loading } = useReunionMensuelleData(fiscalYear);

  const periods = useMemo(() => getPeriodBuckets(fiscalYear, granularity), [fiscalYear, granularity]);
  const columns = useMemo(() => getScopeColumns(scope, agencies), [scope, agencies]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentFiscalYear = currentMonth < 4 ? currentYear - 1 : currentYear;
  const years = Array.from({ length: 11 }, (_, i) => currentFiscalYear - 5 + i);

  const noSectionSelected = !Object.values(sections).some(Boolean);

  const toggleSection = (key: keyof ReunionExportSections) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = async () => {
    if (noSectionSelected) return;
    setExporting(true);
    setExportError('');
    try {
      await exportReunionMensuelle(transactions, agencies, periods, columns, scope, fiscalYear, { sections, includeDetails });
      onClose();
    } catch (err: any) {
      console.error('Error exporting réunion mensuelle data:', err);
      setExportError(err.message || "Une erreur est survenue lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl relative max-h-[92vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <FileSpreadsheet size={18} className="text-white/80" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-tight">Exporter en Excel</h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Période */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Période</p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar size={15} className="text-slate-400" />
                <select value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value, 10))} className={SELECT_CLS}>
                  {years.map(y => <option key={y} value={y}>Année {y} – {y + 1}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                {([
                  { id: 'month', label: 'Par mois', icon: CalendarDays },
                  { id: 'quarter', label: 'Par trimestre', icon: CalendarRange },
                  { id: 'year', label: 'Par année', icon: Calendar },
                ] as { id: Granularity; label: string; icon: any }[]).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setGranularity(id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      granularity === id ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Périmètre */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Sociétés / agences</p>
            <ScopeSelector scope={scope} onChange={setScope} agencies={agencies} />
          </div>

          {/* Données à exporter */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Données à exporter</p>
            <div className="flex flex-wrap gap-2">
              {SECTION_OPTIONS.map(opt => {
                const checked = sections[opt.key];
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => toggleSection(opt.key)}
                    className={`px-3 py-2 text-sm font-medium rounded-xl border transition-colors ${
                      checked
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {noSectionSelected && (
              <p className="text-xs text-red-500 mt-1.5">Sélectionnez au moins une catégorie de données.</p>
            )}
          </div>

          {/* Détail */}
          <div>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={includeDetails}
                onChange={(e) => setIncludeDetails(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400 cursor-pointer"
              />
              <span className="text-sm text-slate-700">
                Inclure le détail des transactions
                <span className="block text-xs text-slate-400 mt-0.5">Ajoute une feuille listant chaque dossier (équivalent de la fiche détail affichée au clic sur le tableau)</span>
              </span>
            </label>
          </div>

          {exportError && <div className="text-sm text-red-600">{exportError}</div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleExport}
            disabled={loading || exporting || noSectionSelected}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md shadow-indigo-200 hover:from-indigo-700 hover:to-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting || loading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {exporting ? 'Export en cours…' : loading ? 'Chargement…' : 'Exporter'}
          </button>
        </div>
      </div>
    </div>
  );
}
